import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import useFetch from "../../../hooks/useFetch";
import HAFHOnboarding from "./HAFHOnboarding";

const ROLE_OPTIONS = [
  "STUDENT", "PARENT", "OWNER", "MANAGER", "INSTITUTION",
  "ADMIN", "MESS", "TRANSPORTER", "DIRECTOR", "TRUSTEE",
  "HUMA", "COORDINATOR",
];

const ONBOARDING_TYPES = ["SELF", "INSTITUTIONAL", "BUSINESS", "ADMIN"];

const SOURCE_CHANNELS = [
  "SELF", "DIRECTOR", "HUMA", "INSTITUTION",
  "SOCIAL_MEDIA", "WEBSITE", "CAMPAIGN", "OTHER",
];

const HAFHOnboardingProfile = ({ t, selectedLanguage, role }) => {
  const authState = useSelector((state) => state.authReducer || {});
  const [fetchData] = useFetch();

  const userId = authState?.user?._id || null;
  const userCode = authState?.user?.think5whyscode || "";
  const userName = authState?.user?.name || "";
  const token = authState?.token;

  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(null);
  const [editId, setEditId] = useState(null);
  const [msg, setMsg] = useState("");
  const [isUpdated, setIsUpdated] = useState(false);
  const [showExtraInputs, setShowExtraInputs] = useState(false);
  const [isOnboarded, setIsOnboarded] = useState(false);
  const [activeRoleTab, setActiveRoleTab] = useState(null);

  const isEditMode = Boolean(editId);
  const isReadOnly = isEditMode;
  const [showFees, setShowFees] = useState(false);

  const [fieldErrors, setFieldErrors] = useState({});

  const buildFilledForm = (record, userCode, userName) => ({
    // USER IDENTITY
    userId: record.userId || "",
    userCode: record.userCode || userCode || "",
    userName: record.userName || userName || "",
    mobileNumber: record.mobileNumber || "",
    emailId: record.emailId || "",
    // ROLE SYSTEM
    roles: record.roles || [],
    preferredActiveRole: record.preferredActiveRole || "STUDENT",
    // FAST ROLE FLAGS
    isStudent: record.isStudent ?? false,
    isParent: record.isParent ?? false,
    isOwner: record.isOwner ?? false,
    isManager: record.isManager ?? false,
    isInstitution: record.isInstitution ?? false,
    isMess: record.isMess ?? false,
    isTrustee: record.isTrustee ?? false,
    isDirector: record.isDirector ?? false,
    isHUMA: record.isHUMA ?? false,
    isAdmin: record.isAdmin ?? false,
    isTransporter: record.isTransporter ?? false,
    isCoordinator: record.isCoordinator ?? false,
    // ONBOARDING SOURCE
    onboardingType: record.onboardingType || "SELF",
    onboardedByCode: record.onboardedByCode || "SELF",
    onboardedByName: record.onboardedByName || "Self Onboarding",
    referredBy: record.referredBy || null,
    referredByCode: record.referredByCode || "",
    referredByName: record.referredByName || "",
    sourceChannel: record.sourceChannel || "SELF",
    // INSTITUTION LINKAGE
    institutionCode: record.institutionCode || "",
    institutionName: record.institutionName || "",
    // HOSTEL LINKAGE
    hostelCode: record.hostelCode || "",
    hostelName: record.hostelName || "",
    // STUDENT RELATIONSHIP
    studentCode: record.studentCode || "",
    studentName: record.studentName || "",
    studentNickNameByParent: record.studentNickNameByParent || "",
    studentNickNameByHostel: record.studentNickNameByHostel || "",
    studentParentCode: record.studentParentCode || "",
    studentParentName: record.studentParentName || "",
    studentOwnerCode: record.studentOwnerCode || "",
    studentManagerCode: record.studentManagerCode || "",
    studentMessCode: record.studentMessCode || "",
    studentTransportCode: record.studentTransportCode || "",
    studentHUMACode: record.studentHUMACode || "",
    studentDirectorCode: record.studentDirectorCode || "",
    studentTrusteeCode: record.studentTrusteeCode || "",
    studentCoordinatorCode: record.studentCoordinatorCode || "",
    // DOCUMENTS
    documentLinks: Array.isArray(record.documentLinks)? record.documentLinks: [],
    // FEES & PAYMENT
    onboardingFeeStudent: record.onboardingFeeStudent ?? 0,
    onboardingFeeParent: record.onboardingFeeParent ?? 0,
    onboardingFeeOwner: record.onboardingFeeOwner ?? 100,
    onboardingFeeTrustee: record.onboardingFeeTrustee ?? 200,
    onboardingFeeInstitution: record.onboardingFeeInstitution ?? 5000,
    onboardingFeeMess: record.onboardingFeeMess ?? 500,
    onboardingFeeTransporter: record.onboardingFeeTransporter ?? 500,
    onboardingFeeAdmin: record.onboardingFeeAdmin ?? 0,
    onboardingFeeDirector: record.onboardingFeeDirector ?? 1000,
    onboardingFeeCoordinator: record.onboardingFeeCoordinator ?? 250,
    onboardingFeeHUMA: record.onboardingFeeHUMA ?? 250,
    paymentRequired: record.paymentRequired ?? false,
    paymentStatus: record.paymentStatus || "PENDING",
    paymentAmount: record.paymentAmount ?? 0,
    paymentDate: record.paymentDate || null,
    transactionId: record.transactionId || "",
    serviceFeePercent: record.serviceFeePercent ?? 0,
    // ACTIVATION
    activationStatus: record.activationStatus || "PENDING",
    activatedBy: record.activatedBy || null,
    activationDate: record.activationDate || null,
    activationEndDate: record.activationEndDate || null,
    rejectionReason: record.rejectionReason || "",
    // BULK ENROLLMENT
    bulkEnrollmentId: record.bulkEnrollmentId || "",
    uploadedBy: record.uploadedBy || null,
    // AGREEMENT
    termsAccepted: record.termsAccepted ?? true,
    termsAcceptedDate: record.termsAcceptedDate || null,
    // ACCOUNT FLAGS
    isDemoAccount: record.isDemoAccount ?? true,
    demoDays: record.demoDays ?? 7,
    isVerified: record.isVerified ?? false,
    isActive: record.isActive ?? true,
    // EXTRA
    remarks: record.remarks || "",
  });

  const fetchMyOnboarding = async () => {
    try {
      setLoading(true);

      const res = await fetchData({
        url: "/hafh/business/onboarding/myall",
        method: "post",
        data: { userId },
        headers: { Authorization: token },
      });

      if (res?.status && res.record) {
        setForm(res.record);
        setIsOnboarded(true);
        setEditId(res.record._id);
      } else {
        setForm(null);
        setIsOnboarded(false);
        setEditId(null);
      }

    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userId) fetchMyOnboarding();
  }, [userId]);

  // ROLE SELECT
  const selectRole = (r) => {
    if (isReadOnly) return;
    setForm((prev) => ({
      ...prev,
      preferredActiveRole:
        prev.preferredActiveRole === r ? "STUDENT" : r,
    }));
  };

  const handleUpdate = async () => {
    const errors = {};

    studentMappingFields.forEach((field) => {
      const value = form?.[field.key]?.trim();

      // validate only if something entered
      if (field.type === "code" && value) {

        const isValid =
          value.length === 14 &&
          value.startsWith("T");

        if (!isValid) {
          errors[field.key] =
            `${field.label} must be blank OR exactly 14 characters starting with T`;
        }
      }
    });

    setFieldErrors(errors);

    // STOP API CALL
    if (Object.keys(errors).length > 0) {
      return;
    }


    try {
      setLoading(true);
      setMsg("");

      const editablePayload = {
        studentCode: form.studentCode,
        studentName: form.studentName,

        hostelCode: form.hostelCode,
        hostelName: form.hostelName,

        institutionCode: form.institutionCode,
        institutionName: form.institutionName,

        studentNickNameByParent: form.studentNickNameByParent,
        studentNickNameByHostel: form.studentNickNameByHostel,

        studentParentCode: form.studentParentCode,
        studentParentName: form.studentParentName,

        studentOwnerCode: form.studentOwnerCode,
        studentManagerCode: form.studentManagerCode,
        studentMessCode: form.studentMessCode,
        studentTransportCode: form.studentTransportCode,
        studentHUMACode: form.studentHUMACode,
        studentDirectorCode: form.studentDirectorCode,
        studentTrusteeCode: form.studentTrusteeCode,
        studentCoordinatorCode: form.studentCoordinatorCode,

        activationStatus: form.activationStatus,
        rejectionReason: form.rejectionReason,

        documentLinks: form.documentLinks,

        serviceFeePercent: form.serviceFeePercent,

        onboardingFeeStudent: form.onboardingFeeStudent,
        onboardingFeeParent: form.onboardingFeeParent,
        onboardingFeeOwner: form.onboardingFeeOwner,
        onboardingFeeTrustee: form.onboardingFeeTrustee,
        onboardingFeeInstitution: form.onboardingFeeInstitution,
        onboardingFeeMess: form.onboardingFeeMess,
        onboardingFeeTransporter: form.onboardingFeeTransporter,
        onboardingFeeAdmin: form.onboardingFeeAdmin,
        onboardingFeeDirector: form.onboardingFeeDirector,
        onboardingFeeCoordinator: form.onboardingFeeCoordinator,
        onboardingFeeHUMA: form.onboardingFeeHUMA,

        paymentRequired: form.paymentRequired,
        paymentDate: form.paymentDate,
        paymentAmount: form.paymentAmount,
        paymentStatus: form.paymentStatus,
        transactionId: form.transactionId,

        activationDate: form.activationDate,
        activationEndDate: form.activationEndDate,

        bulkEnrollmentId: form.bulkEnrollmentId,
        uploadedBy: form.uploadedBy,

        termsAccepted: form.termsAccepted,
        termsAcceptedDate: form.termsAcceptedDate,

        isDemoAccount: form.isDemoAccount,
        demoDays: form.demoDays,
        isVerified: form.isVerified,
        isActive: form.isActive,
      };

      const res = await fetchData({
        url: "/hafh/business/onboarding/update",
        method: "put",
        data: editablePayload,
        headers: { Authorization: token },
      });

      if (res?.status) {
        setMsg("✅ Onboarding updated successfully");
        setIsUpdated(true);

        if (res.record) {
          setForm(buildFilledForm(res.record));
        }
      } else {
        setMsg(res?.msg || "❌ Update failed");
        setIsUpdated(false);
      }

    } catch (err) {
      setMsg("❌ Server error while updating");
      setIsUpdated(false);

    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-gray-900 p-5 rounded-xl text-white">
        Loading onboarding...
      </div>
    );
  }

  const Input = ({ label, value, onChange }) => (
    <div className="flex flex-col gap-1">
      <label className="text-xs lg:text-sm text-gray-100">{label}</label>
      <input
        className="w-full bg-gray-700 p-1 rounded-md text-xs lg:text-sm"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );

  const SectionTitle = ({ title }) => (
    <div className="col-span-1 lg:col-span-2 mt-2">
      <h4 className="text-xs lg:text-sm text-cyan-200 font-semibold">{title}</h4>
    </div>
  );

  const handleCodeChange = (key, value) => {
    let cleaned = value
      .toUpperCase()
      .replace(/\s/g, "");

    // Always force first character as T
    if (!cleaned.startsWith("T")) {
      cleaned = "T" + cleaned.replace(/^T/i, "");
    }

    cleaned = cleaned.slice(0, 14);

    setForm((prev) => ({
      ...prev,
      [key]: cleaned,
    }));
  };

  const studentMappingFields = [
    { key: "studentParentCode", label: "Student Parent Code", type: "code" },
    { key: "studentOwnerCode", label: "Owner Code", type: "code" },
    { key: "studentManagerCode", label: "Manager Code", type: "code" },
    { key: "studentMessCode", label: "Mess Code", type: "code" },
    { key: "studentTransportCode", label: "Transport Code", type: "code" },
    { key: "studentHUMACode", label: "HUMA Code", type: "code" },
    { key: "studentDirectorCode", label: "Director Code", type: "code" },
    { key: "studentTrusteeCode", label: "Trustee Code", type: "code" },
    { key: "studentCoordinatorCode", label: "Coordinator Code", type: "code" },
  ];

  // setFieldErrors(errors);

  // if (Object.keys(errors).length > 0) {
  //   return;
  // }

  const feeFields = [
    { key: "serviceFeePercent", label: "Service Fee %" },
    { key: "onboardingFeeStudent", label: "Onboarding Fee Student" },
    { key: "onboardingFeeParent", label: "Onboarding Fee Parent" },
    { key: "onboardingFeeOwner", label: "Onboarding Fee Owner" },
    { key: "onboardingFeeTrustee", label: "Onboarding Fee Trustee" },
    { key: "onboardingFeeInstitution", label: "Onboarding Fee Institution" },
    { key: "onboardingFeeMess", label: "Onboarding Fee Mess" },
    { key: "onboardingFeeTransporter", label: "Onboarding Fee Transporter" },
    { key: "onboardingFeeAdmin", label: "Onboarding Fee Admin" },
    { key: "onboardingFeeDirector", label: "Onboarding Fee Director" },
    { key: "onboardingFeeCoordinator", label: "Onboarding Fee Coordinator" },
    { key: "onboardingFeeHUMA", label: "Onboarding Fee HUMA" },
  ];

  return (
    <div className="space-y-6 bg-gray-900 p-6 rounded-xl text-white">   
      {msg && (
        <div className="text-sm text-green-400">{msg}</div>
      )}

      <div className="flex flex-col gap-1">
        <h2 className="text-yellow-200">ACTIVE/ASSIGNED ROLES</h2>
        {form?.roles?.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {form.roles.map((role, index) => {
              const isPreferred = role === form?.preferredActiveRole;
              return (
                <span
                  key={index}
                  className={`px-3 py-1 rounded-lg text-xs lg:text-sm border transition
                    ${
                      isPreferred
                        ? "bg-yellow-400 text-black font-semibold border-yellow-500"
                        : "bg-cyan-700 text-white border-transparent"
                    }`}
                >
                  {role}
                </span>
              );
            })}
          </div>
        )}
      </div>

      <div className="flex flex-col gap-1">
        <h2 className="text-yellow-200">REMAINING AVAILABLE ROLES</h2>

        <div className="flex flex-row flex-wrap gap-2 mt-2">
          {ROLE_OPTIONS
            .filter((role) => !form?.roles?.includes(role))
            .map((role, index) => (
              <button
                key={index}
                type="button"
                onClick={() => setActiveRoleTab(role)}
                className="w-fit px-3 py-1 rounded-lg text-xs lg:text-sm border border-gray-600 bg-teal-800 text-gray-300 hover:bg-cyan-700 hover:text-white transition"
              >
                {role}
              </button>
            ))}
        </div>
      </div>

      {activeRoleTab && (
        <HAFHOnboarding
          t={t}
          selectedLanguage={selectedLanguage}
          role={activeRoleTab}
          isNew={false}
        />
      )}

      <div>
        <p className="text-xs lg:text-sm text-gray-100">Mobile Number: {form?.mobileNumber}</p>
        <p className="text-xs lg:text-sm text-gray-100">Email ID: {form?.emailId}</p>
      </div>

      {/* ONBOARDING TYPE */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-2">
        <p className="text-sm text-gray-100 font-medium">
          Onboarding Type: {form?.onboardingType}
        </p>
        <p className="text-sm text-gray-100 font-medium">
          Source Channel: {form?.sourceChannel}
        </p>
        {/* <h3 className="text-cyan-300 text-sm lg:text-lg font-semibold">
          Referral Information
        </h3> */}
        <p className="text-gray-100">
          Referred BY: {form?.referredByName} {" "}{form?.referredByCode}
        </p>
        <p className="text-gray-100">
          Onboarded BY: {form?.onboardedByCode} {" "}{form?.onboardedByName}
        </p>
      </div>

        <div className="bg-gray-800 p-5 rounded-xl space-y-2 border-t border-yellow-100">
          <h3 className="text-cyan-300 text-sm lg:text-lg font-semibold">Additional Details</h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-2">
            <div className="flex flex-col gap-1">
              <label className="text-xs lg:text-sm text-gray-100">Student Code</label>
              <input
                className="bg-gray-700 p-2 rounded-md w-[280px] tracking-widest text-xs lg:text-sm"
                value={form?.studentCode}
                onChange={(e) =>
                  setForm({ ...form, studentCode: e.target.value })
                }
              />
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-xs lg:text-sm text-gray-100">Student Name</label>
              <input
                className="w-full bg-gray-700 p-1 rounded-md text-xs lg:text-sm"
                value={form?.studentName}
                onChange={(e) =>
                  setForm({ ...form, studentName: e.target.value })
                }
              />
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-xs lg:text-sm text-gray-100">Hostel Code</label>
              <input
                className="bg-gray-700 p-1 rounded-md w-[280px] tracking-widest text-xs lg:text-sm"
                value={form?.hostelCode}
                onChange={(e) =>
                  setForm({ ...form, hostelCode: e.target.value })
                }
              />
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-xs lg:text-sm text-gray-100">Hostel Name</label>
              <input
                className="w-full bg-gray-700 p-1 rounded-md text-xs lg:text-sm"
                value={form?.hostelName}
                onChange={(e) =>
                  setForm({ ...form, hostelName: e.target.value })
                }
              />
            </div>
              
            <div className="flex flex-col gap-1">
              <label className="text-xs lg:text-sm text-gray-100">Institution Code</label>
              <input
                className="bg-gray-700 p-1 rounded-md w-[280px] tracking-widest text-xs lg:text-sm"
                value={form?.institutionCode}
                onChange={(e) =>
                  setForm({ ...form, institutionCode: e.target.value })
                }
              />
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-xs lg:text-sm text-gray-100">Institution Name</label>
              <input
                className="w-full bg-gray-700 p-1 rounded-md text-xs lg:text-sm"
                value={form?.institutionName}
                onChange={(e) =>
                  setForm({ ...form, institutionName: e.target.value })
                }
              />
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-2 border-t border-yellow-100"></div>
            <h4 className="text-sm lg:text-lg text-cyan-200 font-semibold mb-2">
              Onboarding Relationship Mapping
            </h4>
            <div className="col-span-1 lg:col-span-2">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-2">
                <div className="flex flex-col gap-1">
                  <label className="text-xs lg:text-sm text-gray-100">Student Nick Name (At Home)</label>
                  <input
                    className="w-full bg-gray-700 p-1 rounded-md text-xs lg:text-sm"
                    value={form?.studentNickNameByParent}
                    onChange={(e) =>
                      setForm({ ...form, studentNickNameByParent: e.target.value })
                    }
                  />
                </div>

                <div className="flex flex-col gap-1">
                  <label className="text-xs lg:text-sm text-gray-100">Student Nick Name (In Hostel)</label>
                  <input
                    className="w-full bg-gray-700 p-1 rounded-md text-xs lg:text-sm"
                    value={form?.studentNickNameByHostel}
                    onChange={(e) =>
                      setForm({ ...form, studentNickNameByHostel: e.target.value })
                    }
                  />
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-2">
              {studentMappingFields.map((field) => (
                <div key={field.key} className="flex flex-col gap-1">

                  <label className="text-xs lg:text-sm text-gray-100">
                    {field.label}
                  </label>

                  <input
                    maxLength={14}
                    className={`bg-gray-700 p-1 rounded-md w-[280px] tracking-widest text-xs lg:text-sm border ${
                      fieldErrors[field.key]
                        ? "border-red-500"
                        : "border-gray-600"
                    }`}
                    value={form?.[field.key] || ""}
                    onChange={(e) => {
                      const value = e.target.value;

                      handleCodeChange(field.key, value);

                      // clear field error while typing
                      setFieldErrors((prev) => ({
                        ...prev,
                        [field.key]: "",
                      }));
                    }}
                  />

                  {fieldErrors[field.key] && (
                    <p className="text-red-400 text-xs">
                      {fieldErrors[field.key]}
                    </p>
                  )}

                </div>
              ))}
            </div>
          </div>

          <div className="mt-2 p-2 border-t border-yellow-100">
            <h4 className="text-sm lg:text-lg text-cyan-200 font-semibold mb-2">
              Profile Status
            </h4>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-2">
              <div className="flex flex-col gap-1">
                <label className="text-xs lg:text-sm text-gray-100">
                  Is Demo Account
                </label>

                <select
                  className="bg-gray-700 p-1 rounded-md text-xs lg:text-sm"
                  value={form?.isDemoAccount ? "true" : "false"}
                  onChange={(e) =>
                    setForm({ ...form, isDemoAccount: e.target.value === "true" })
                  }
                >
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              </div>                
              <div className="flex flex-col gap-1">
                <p className="text-xs lg:text-sm text-gray-100 mt-2">Demo Days</p>
                <p className="text-xs lg:text-sm text-gray-100 p-2">{form?.demoDays}</p>
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs lg:text-sm text-gray-100">Is Verified</label>
                <select
                  className="bg-gray-700 p-1 rounded-md text-xs lg:text-sm"
                  value={form?.isVerified ? "true" : "false"}
                  onChange={(e) =>
                    setForm({ ...form, isVerified: e.target.value === "true" })
                  }
                >
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-xs lg:text-sm text-gray-100">Is Active</label>
                <select
                  className="bg-gray-700 p-1 rounded-md text-xs lg:text-sm"
                  value={form?.isActive ? "true" : "false"}
                  onChange={(e) =>
                    setForm({ ...form, isActive: e.target.value === "true" })
                  }
                >
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs lg:text-sm text-gray-100">Profile Activation Status</label>
                <select
                  className="w-full bg-gray-700 p-1 rounded-md"
                  value={form?.activationStatus}
                  onChange={(e) =>
                    setForm({ ...form, activationStatus: e.target.value })
                  }
                >
                  <option value="PENDING">PENDING</option>
                  <option value="UNDER_REVIEW">UNDER_REVIEW</option>
                  <option value="APPROVED">APPROVED</option>
                  <option value="REJECTED">REJECTED</option>
                  <option value="ACTIVE">ACTIVE</option>
                  <option value="DEACTIVATED">DEACTIVATED</option>
                  <option value="TERMINATED">TERMINATED</option>
                </select>
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-xs lg:text-sm text-gray-100">Rejection Reason / Remarks</label>
                <input
                  className="w-full bg-gray-700 p-1 rounded-md text-xs lg:text-sm"
                  value={form?.rejectionReason}
                  onChange={(e) =>
                    setForm({ ...form, rejectionReason: e.target.value })
                  }
                />
              </div>

              <div className="col-span-1 lg:col-span-2 gap-1">
                <label className="text-xs lg:text-sm text-gray-100">Remarks</label>
                <textarea
                  className="w-full bg-gray-700 p-1 rounded-md"
                  style={{height: "150px"}}
                  value={form?.remarks}
                  onChange={(e) =>
                    setForm({ ...form, remarks: e.target.value })
                  }
                />
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-between items-center mt-4">
          <button
            disabled={isUpdated}
            onClick={() => setShowExtraInputs((prev) => !prev)}
            className="text-xs lg:text-sm text-gray-100 bg-teal-900 p-2"
          >
            {showExtraInputs ? "Hide Other Additional Inputs" : "Other Additional Inputs"}
          </button>
        </div>

        {showExtraInputs && (
          <div className="bg-gray-800 p-2 rounded-xl space-y-2 mt-2 border border-gray-700">
            <h3 className="text-cyan-300 text-sm lg:text-lg font-semibold">
              Other Additional Onboarding Inputs
            </h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <div>
                <label className="text-xs lg:text-sm text-gray-100">Document Links (comma separated)</label>
                <input
                  className="w-full bg-gray-700 p-1 rounded-md text-xs lg:text-sm"
                  value={form?.documentLinks?.join(",") || ""}
                  onChange={(e) =>
                    setForm({ ...form, documentLinks: e.target.value.split(",") })
                  }
                />
              </div>
            </div>

            {/* ================= FEES ================= */}
            <div className="mt-2 border-t border-yellow-100">
              <h2 className="text-yellow-200 text-sm lg:text-lg font-semibold cursor-pointer"
                onClick={() => setShowFees((prev) => !prev)}
              >
                Onboarding Fees
              </h2>

              {showFees && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mt-3">
                  {feeFields.map((field) => (
                    <div key={field.key} className="bg-gray-800 p-1 rounded-md">
                      <p className="text-xs lg:text-sm text-gray-200">
                        {field.label}
                      </p>
                      <p className="text-white font-semibold">
                        {Number(form?.[field.key] || 0).toFixed(2)}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* ================= PAYMENT ================= */}
            <div className="mt-2 border-t border-yellow-100">
              <h2 className="text-yellow-200 text-sm lg:text-lg  font-semibold">Payment Details</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                <div className="flex flex-col gap-1">
                  <label className="text-xs lg:text-sm text-gray-100">
                    Payment Required
                  </label>

                  <select
                    className="bg-gray-700 p-1 rounded-md text-xs lg:text-sm"
                    value={form?.paymentRequired ? "true" : "false"}
                    onChange={(e) =>
                      setForm({ ...form, paymentRequired: e.target.value === "true" })
                    }
                  >
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-xs lg:text-sm text-gray-100">
                    Payment Date
                  </label>

                  <input
                    type="date"
                    className="w-full bg-gray-700 p-2 rounded-md text-xs lg:text-sm"
                    value={form?.paymentDate ? form.paymentDate.slice(0, 10) : ""}
                    onChange={(e) =>
                      setForm({ ...form, paymentDate: e.target.value })
                    }
                  />
                </div>              
                <div>
                  <label className="text-xs lg:text-sm text-gray-100">Payment Amount</label>
                  <input
                    type="number"
                    className="w-full bg-gray-700 p-1 rounded-md text-xs lg:text-sm"
                    value={form?.paymentAmount}
                    onChange={(e) =>
                      setForm({ ...form, paymentAmount: e.target.value })
                    }
                  />
                </div>
                <div>
                  <label className="text-xs lg:text-sm text-gray-100">Payment Status</label>
                  <select
                    className="w-full bg-gray-700 p-1 rounded-md"
                    value={form?.paymentStatus}
                    onChange={(e) =>
                      setForm({ ...form, paymentStatus: e.target.value })
                    }
                  >
                    <option value="PENDING">PENDING</option>
                    <option value="PAID">PAID</option>
                    <option value="FAILED">FAILED</option>
                    <option value="REFUNDED">REFUNDED</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs lg:text-sm text-gray-100">Transaction ID</label>
                  <input
                    className="w-full bg-gray-700 p-1 rounded-md text-xs lg:text-sm"
                    value={form?.transactionId}
                    onChange={(e) =>
                      setForm({ ...form, transactionId: e.target.value })
                    }
                  />
                </div>
              </div>
            </div>

            {/* ================= ACTIVITY ================= */}
            <div className="mt-2 border-t border-yellow-100">
              <h2 className="text-yellow-200 text-sm lg:text-lg font-semibold">Activation Details</h2>         
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                <div className="flex flex-col gap-1">
                  <label className="text-xs lg:text-sm text-gray-100">Activated By</label>
                  <input
                    className="w-full bg-gray-700 p-1 rounded-md text-xs lg:text-sm"
                    value={form?.activatedBy}
                    onChange={(e) =>
                      setForm({ ...form, activatedBy: e.target.value })
                    }
                  />
                </div>              
                <div className="flex flex-col gap-1">
                  <label className="text-xs lg:text-sm text-gray-100">
                    Activation Date
                  </label>

                  <input
                    type="date"
                    className="w-full bg-gray-700 p-2 rounded-md text-xs lg:text-sm"
                    value={form?.activationDate ? form.activationDate.slice(0, 10) : ""}
                    onChange={(e) =>
                      setForm({ ...form, activationDate: e.target.value })
                    }
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-xs lg:text-sm text-gray-100">
                    Activation End Date
                  </label>

                  <input
                    type="date"
                    className="w-full bg-gray-700 p-2 rounded-md text-xs lg:text-sm"
                    value={form?.activationEndDate ? form.activationEndDate.slice(0, 10) : ""}
                    onChange={(e) =>
                      setForm({ ...form, activationEndDate: e.target.value })
                    }
                  />
                </div>              
              </div>
            </div>

              {/* ================= BULK ================= */}
              {/* <div className="mt-2 border-t border-yellow-100">
                <h2 className="text-yellow-200 text-sm lg:text-lg font-semibold">Bulk Operations</h2>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                  <div className="flex flex-col gap-1">
                    <label className="text-xs lg:text-sm text-gray-100">Bulk Enrollment ID</label>
                    <input
                      className="w-full bg-gray-700 p-1 rounded-md text-xs lg:text-sm"
                      value={form?.bulkEnrollmentId}
                      onChange={(e) =>
                        setForm({ ...form, bulkEnrollmentId: e.target.value })
                      }
                    />
                  </div>     
                  <div className="flex flex-col gap-1">
                    <label className="text-xs lg:text-sm text-gray-100">Uploaded By</label>
                    <input
                      className="w-full bg-gray-700 p-1 rounded-md text-xs lg:text-sm"
                      value={form?.uploadedBy}
                      onChange={(e) =>
                        setForm({ ...form, uploadedBy: e.target.value })
                      }
                    />
                  </div>     
              </div>
              </div> */}

              {/* ================= AGREEMENT ================= */}
              <div className="mt-2  border-t border-yellow-100">
                <h2 className="text-yellow-200 text-sm lg:text-lg font-semibold">Agreement</h2>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                  <div className="flex flex-col gap-1">
                    <label className="text-xs lg:text-sm text-gray-100">
                      Is Terms Accepted
                    </label>

                    <select
                      className="bg-gray-700 p-1 rounded-md text-xs lg:text-sm"
                      value={form?.termsAccepted ? "true" : "false"}
                      onChange={(e) =>
                        setForm({ ...form, termsAccepted: e.target.value === "true" })
                      }
                    >
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </select>
                  </div>        
                  <div className="flex flex-col gap-1">
                    <label className="text-xs lg:text-sm text-gray-100">
                      Terms Accepted Date
                    </label>

                    <input
                      type="date"
                      className="w-full bg-gray-700 p-2 rounded-md text-xs lg:text-sm"
                      value={form?.termsAcceptedDate ? form.termsAcceptedDate.slice(0, 10) : ""}
                      onChange={(e) =>
                        setForm({ ...form, termsAcceptedDate: e.target.value })
                      }
                    />
                  </div>                 
                </div>
              </div>
          </div>
        )}

        <div className="flex flex-col items-center gap-2">
          <button
            onClick={handleUpdate}
            disabled={isUpdated}
            className="bg-teal-600 hover:bg-teal-800 px-6 py-3 rounded-md font-medium disabled:opacity-80"
          >
            Update Onboarding
          </button>
          {Object.keys(fieldErrors).length > 0 && (
            <div className="text-red-400 text-xs lg:text-sm mt-2 text-center">
              ⚠️ View and correct all errors before updating!
            </div>
          )}
          {msg && (
            <div className="text-xs lg:text-sm text-yellow-300">
              {msg}
            </div>
          )}
        </div>
    </div>
  );
};

export default HAFHOnboardingProfile;